import type { Query } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { QueryKey } from './queryClient';
import { storeKeys } from 'core/storage/store';

// Keeping React Query data across page loads.
//
// A reload starts React Query empty, so a page shows loading states until
// its first responses arrive even though the user saw the same data a
// moment ago. Persisting part of the cache to localStorage lets the next
// load paint the previous content at first paint and refresh it in the
// background: the same "show it, then reload it" rule the mutations follow.
//
// Any query can opt in by adding its key to PERSISTED_KEYS. Today that is
// the dashboard's lists. Left out on purpose: media URLs, which expire and
// have their own rules (see media-caching.md), and the user profile, which
// keeps its own copy in localStorage already. Large or paged data should
// be trimmed on the way out, as the memos lists are below.

// Keep the cache for a day. Everything in it is refreshed on first use
// anyway; this just stops a very old copy from ever being the starting
// point.
export const PERSISTED_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Bump when the shape of a persisted query changes, so old copies are
// dropped instead of restored.
export const PERSISTED_CACHE_VERSION = '3';

const PERSISTED_KEYS = new Set<string>([
  QueryKey.SPACES,
  QueryKey.BOXES,
  QueryKey.COLLECTIONS,
  QueryKey.BOX_STATS,
  QueryKey.MEMOS,
]);

// Which queries are written to disk: the opted-in keys, and only when they
// hold a good answer.
export const shouldPersistQuery = (query: Query): boolean =>
  typeof query.queryKey[0] === 'string' &&
  PERSISTED_KEYS.has(query.queryKey[0]) &&
  query.state.status === 'success';

type Pages = { pages: unknown[]; pageParams: unknown[] };

const isPaged = (data: unknown): data is Pages =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray((data as Pages).pages) &&
  Array.isArray((data as Pages).pageParams);

// A memos list can be many pages deep after scrolling. Only the first page
// is needed to paint the list again, and it keeps the stored copy small.
// Other paged queries that opt in later should be trimmed here too.
export const keepFirstPageOnly = (
  client: PersistedClient,
): PersistedClient => ({
  ...client,
  clientState: {
    ...client.clientState,
    queries: client.clientState.queries.map((query) => {
      if (query.queryKey[0] !== QueryKey.MEMOS || !isPaged(query.state.data)) {
        return query;
      }
      const { pages, pageParams } = query.state.data;
      return {
        ...query,
        state: {
          ...query.state,
          data: {
            pages: pages.slice(0, 1),
            pageParams: pageParams.slice(0, 1),
          },
        },
      };
    }),
  },
});

// Most queries kept on disk. Enough for every list of a few spaces and their
// boxes; past that, the least recently loaded are dropped.
export const MAX_PERSISTED_QUERIES = 100;

type PersistedQuery = PersistedClient['clientState']['queries'][number];

// Builds the copy to write to localStorage, combining this tab's queries
// with what is already saved there.
//
// Why: all MindFeed tabs in the browser share one saved copy. Say tab A shows
// box 1 and tab B shows box 2. Each tab only knows its own lists, so if
// saving simply replaced the copy, whichever tab saved last would wipe the
// other's lists. Reloading tab A would then find box 2 saved but not box 1,
// and box 1's memos would have to wait for the network.
//
// How: start from what is saved, add this tab's queries, and when both have
// the same query (the same box's memos, say) keep the one that was loaded
// most recently. A saved copy written by an older version of the app is
// ignored, since its data may not have the shape the app expects now.
export const mergeWithStored = (
  client: PersistedClient,
  stored: PersistedClient | null,
): PersistedClient => {
  const byHash = new Map<string, PersistedQuery>();
  const keep = (query: PersistedQuery) => {
    const current = byHash.get(query.queryHash);
    if (!current || query.state.dataUpdatedAt >= current.state.dataUpdatedAt) {
      byHash.set(query.queryHash, query);
    }
  };

  if (stored?.buster === client.buster) {
    stored.clientState.queries.forEach(keep);
  }
  client.clientState.queries.forEach(keep);

  const queries = [...byHash.values()]
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    .slice(0, MAX_PERSISTED_QUERIES);

  return { ...client, clientState: { ...client.clientState, queries } };
};

const storage = typeof window !== 'undefined' ? window.localStorage : undefined;

// What's on disk now, or null if there's nothing usable.
const readStored = (): PersistedClient | null => {
  try {
    const raw = storage?.getItem(storeKeys.rqCache);
    return raw ? (JSON.parse(raw) as PersistedClient) : null;
  } catch {
    return null;
  }
};

// localStorage is only there in the browser. Without a storage the
// persister does nothing, which is what we want during server rendering.
export const persister = createSyncStoragePersister({
  storage,
  key: storeKeys.rqCache,
  serialize: (client) =>
    JSON.stringify(keepFirstPageOnly(mergeWithStored(client, readStored()))),
});
